<template>
  <ConfirmDialog
    :group="currentGroup"
    :pt="{
      root: { 'data-testid': 'confirm-dialog' },
      pcAcceptButton: { 'data-testid': 'confirm-accept' },
      pcRejectButton: { 'data-testid': 'confirm-reject' },
    }"
  />
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import type { ToastMessageOptions } from 'primevue';
import { useConfirm } from 'primevue/useconfirm';

import i18n from '@/plugins/i18n';

const { t } = i18n.global;
const confirm = useConfirm();

const currentGroup = ref<string>();

type ConfirmMessage = {
  message: string;
  header: string;
  icon: string;
  acceptLabel: string;
  rejectLabel: string;
  severity: ToastMessageOptions['severity'];
};

export type PartialConfirmMessage = Partial<ConfirmMessage>;

type ConfirmMessagesMap = {
  [key: string]: ConfirmMessage;
};

function showConfirm(
  group: string,
  {
    header = t('confirm.default.header'),
    message = t('confirm.default.message'),
    acceptLabel = t('buttons.yes'),
    rejectLabel = t('buttons.no'),
    icon = 'pi pi-exclamation-triangle',
  } = {},
): Promise<boolean> {
  currentGroup.value = group;
  return new Promise((resolve) => {
    // Wait for DOM updates before calling confirm.require
    void nextTick(() => {
      confirm.require({
        group,
        message,
        header,
        icon,
        acceptLabel,
        rejectLabel,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  });
}

function getConfirmMessages(key: string) {
  const messages: ConfirmMessagesMap = {
    delete: {
      header: t('confirm.delete.header'),
      message: t('confirm.delete.message'),
      acceptLabel: t('buttons.delete'),
      rejectLabel: t('buttons.cancel'),
      icon: 'pi pi-exclamation-triangle',
      severity: 'warn',
    },
    save: {
      header: t('confirm.save.header'),
      message: t('confirm.save.message'),
      acceptLabel: t('buttons.save'),
      rejectLabel: t('buttons.cancel'),
      icon: 'pi pi-save',
      severity: 'info',
    },
  };

  return (
    messages[key] || {
      header: t('confirm.default.header'),
      message: t('confirm.default.message'),
      acceptLabel: t('buttons.yes'),
      rejectLabel: t('buttons.no'),
      icon: 'pi pi-info-circle',
      severity: 'info',
    }
  );
}

defineExpose({
  isConfirmed: async (confirmMessages?: PartialConfirmMessage) => {
    try {
      return await showConfirm('unspecific', {
        ...getConfirmMessages('unspecific'),
        ...confirmMessages,
      });
    } finally {
      currentGroup.value = undefined;
    }
  },
  isDeleteConfirmed: async (confirmMessages?: PartialConfirmMessage) => {
    try {
      return await showConfirm('deleteConfirm', {
        ...getConfirmMessages('delete'),
        ...confirmMessages,
      });
    } finally {
      currentGroup.value = undefined;
    }
  },
  isResetConfirmed: async (confirmMessages?: PartialConfirmMessage) => {
    try {
      return await showConfirm('resetConfirm', {
        ...getConfirmMessages('reset'),
        ...confirmMessages,
      });
    } finally {
      currentGroup.value = undefined;
    }
  },
});
</script>
